import { doc, collection, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Live extendedAccess grant + the Cloud-Function-only extendedAccessStudents
// write.
//
// firestore.rules: isAnyAdmin() OR a caseload writer who passes
// canManageExtendedAccess(studentId) — i.e. the student EXISTS and names them
// as case manager / staff. So the owning-CM path can only be probed when this
// role can also create the student to own. Plain case_manager and
// speech_case_manager cannot create students, so that positive path is
// reported as SKIPPED rather than dressed up as a correct denial: a denial
// there would come from the missing student doc, not from the role.

const CASELOAD_WRITER_ROLES = ['case_manager', 'case_manager_full', 'speech_case_manager']

function grantPayload(ctx, studentId, schoolId) {
  return {
    studentId,
    enabled: true,
    teachers: [],
    aides: [],
    caseManagers: [],
    serviceProviders: [],
    allowedData: { basicInfo: true, schedule: true, documents: false },
    __smokeTest: true,
    __smokeRunId: ctx.runId,
    createdAt: serverTimestamp(),
    ...(schoolId ? { schoolId } : {})
  }
}

function studentPayload(ctx, caseManagerId) {
  return {
    app: {
      studentData: {
        firstName: 'SMOKE',
        lastName: `EA ${ctx.runId.slice(-8)}`,
        plan: 'IEP',
        grade: '9',
        ssid: `SMOKE-EA-${ctx.runId}`,
        caseManagerId
      },
      accommodations: { instruction: '', assessment: '' },
      schedule: { classServices: [] },
      staffIds: []
    },
    __smokeTest: true,
    __smokeRunId: ctx.runId,
    createdAt: serverTimestamp(),
    ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
  }
}

export default {
  id: 'extendedAccess',
  label: 'Extended access grant',
  description:
    'extendedAccess create/read/delete for your role; extendedAccessStudents stays Cloud-Function-only.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'extended access write',
        'Select a school before probing school-scoped extended access'
      )
      return
    }

    const canCreateStudent = RULE_EXPECTATIONS.studentCreate(role)
    const studentRef = doc(collection(db, 'students'))
    // Until a student is created, the grant targets an id with no student doc —
    // canManageExtendedAccess() then denies for every non-admin role.
    let studentId = `smoke-ea-${ctx.runId.slice(-8)}`
    let studentCreated = false
    let grantCreated = false

    try {
      if (canCreateStudent) {
        await step('create owned smoke student for grant (expected: allowed)', async () => {
          const { allowed } = await attempt(() =>
            setDoc(studentRef, studentPayload(ctx, ctx.user.uid))
          )
          studentCreated = allowed
          if (allowed) studentId = studentRef.id
          return allowed
            ? `allowed, as expected — students/${studentRef.id}`
            : accessDetail(true, allowed)
        })
      } else if (CASELOAD_WRITER_ROLES.includes(role)) {
        step.skip(
          'owning-case-manager grant',
          `${role} cannot create a student to own, so the owning-CM grant path cannot be probed from this account. Use a seeded caseload account. The no-relationship denial is still asserted below.`
        )
      }

      const grantRef = doc(db, 'extendedAccess', studentId)
      const expectWrite = RULE_EXPECTATIONS.extendedAccessWrite(role, {
        isManagerOfStudent: studentCreated
      })
      const writeLabel = studentCreated
        ? `write grant for a student you own (expected: ${fmtExpected(expectWrite)})`
        : `write grant with no student relationship (expected: ${fmtExpected(expectWrite)})`

      await step(writeLabel, async () => {
        const { allowed } = await attempt(() =>
          setDoc(grantRef, grantPayload(ctx, studentId, ctx.schoolId))
        )
        grantCreated = allowed
        return allowed
          ? `${accessDetail(expectWrite, allowed)} — extendedAccess/${studentId}`
          : accessDetail(expectWrite, allowed)
      })

      if (!grantCreated) {
        step.skip('read/delete grant', 'Grant was not written — later steps skipped')
      } else {
        await step('read grant back (expected: allowed)', async () => {
          const { allowed } = await attempt(async () => {
            const snap = await getDoc(grantRef)
            if (!snap.exists()) throw new Error('Created grant not found on read-back')
          })
          return accessDetail(true, allowed)
        })

        await step(`delete grant (expected: ${fmtExpected(expectWrite)})`, async () => {
          const { allowed } = await attempt(() => deleteDoc(grantRef))
          return accessDetail(expectWrite, allowed)
        })
      }

      // Function-written mirror: no client role may write it, ever.
      const mirrorExpected = RULE_EXPECTATIONS.extendedAccessStudentsWrite()
      const mirrorRef = doc(db, 'extendedAccessStudents', studentId)
      await step(
        `extendedAccessStudents write (expected: ${fmtExpected(mirrorExpected)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(mirrorRef, {
              studentId,
              allowedUsers: [ctx.user.uid],
              __smokeTest: true,
              __smokeRunId: ctx.runId,
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
          )
          if (allowed) await deleteDoc(mirrorRef).catch(() => {})
          return accessDetail(mirrorExpected, allowed)
        }
      )
    } finally {
      await deleteDoc(doc(db, 'extendedAccess', studentId)).catch(() => {})
      await deleteDoc(doc(db, 'extendedAccessStudents', studentId)).catch(() => {})
      if (studentCreated) await deleteDoc(studentRef).catch(() => {})
    }
  }
}
